import { useState, useEffect } from 'react';
import { useChat } from '@edusense/ai-chat/contexts/ChatContext';
import { useDragAndDrop } from '../../../hooks/useDragAndDrop';
import { useResizable } from '../../../hooks/useResizable';
import { usePinchZoom } from '../../../hooks/usePinchZoom';
// Local breakpoints definition
const BREAKPOINTS = {
    sm: 640,
    md: 768,
    lg: 1280,
    xl: 1536
};
export const useAIChatModalState = ({ isSidebarMode })=>{
    // PC 뷰에서 자동 활성화된 경우 감지
    const [isAutoActivated, setIsAutoActivated] = useState(false);
    useEffect(()=>{
        // lg 브레이크포인트 이상에서 사이드바 모드인 경우 자동 활성화로 판단
        if (isSidebarMode && window.innerWidth >= BREAKPOINTS.lg) {
            setIsAutoActivated(true);
        }
    }, [
        isSidebarMode
    ]);
    const { getCurrentChat, sendMessage, isGenerating, stopGeneration, createChat, currentChatId, clearChatMessages } = useChat();
    // 반응형 기준을 md(768px)로 통일해 레이아웃 일관성 확보
    const [isMobile, setIsMobile] = useState(window.innerWidth < BREAKPOINTS.md);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isCompact, setIsCompact] = useState(true); // 항상 compact 모드
    const [position, setPosition] = useState({
        x: window.innerWidth - 420,
        y: 100
    });
    const [size, setSize] = useState({
        width: 400,
        height: 400
    });
    const [currentView, setCurrentView] = useState('chat');
    // Debug state changes
    useEffect(()=>{
        console.log('[ChatModal] currentView changed:', currentView);
    }, [
        currentView
    ]);
    // 드래그 앤 드롭 훅 - sidebar mode에서는 비활성화
    const dragDropResult = isSidebarMode ? {
        isDragging: false,
        handleMouseDown: ()=>{},
        handleTouchStart: ()=>{}
    } : useDragAndDrop({
        position,
        setPosition,
        size,
        isMaximized,
        isMobile
    });
    const { isDragging, handleMouseDown, handleTouchStart: handleDragTouchStart } = dragDropResult;
    // 리사이즈 훅 - sidebar mode에서는 비활성화
    const resizableResult = isSidebarMode ? {
        isResizing: false,
        handleResizeStart: ()=>{}
    } : useResizable({
        position,
        setPosition,
        size,
        setSize,
        isMaximized
    });
    const { isResizing, handleResizeStart } = resizableResult;
    // 핀치 줌 훅 - sidebar mode에서는 비활성화
    const pinchZoomResult = isSidebarMode ? {
        isPinching: false,
        handlePinchStart: ()=>{}
    } : usePinchZoom({
        size,
        setSize,
        isMaximized,
        isMobile
    });
    const { isPinching, handlePinchStart } = pinchZoomResult;
    const currentChat = getCurrentChat();
    // 반응형 처리
    useEffect(()=>{
        const handleResize = ()=>{
            const mobile = window.innerWidth < BREAKPOINTS.md;
            console.log('[ChatModal] Window resize detected', {
                width: window.innerWidth,
                mobile
            });
            setIsMobile(mobile);
            if (mobile) {
                setIsMaximized(true); // 모바일에서는 항상 최대화
                setIsCompact(true);
                setSize({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
                setPosition({
                    x: 0,
                    y: 0
                });
            } else {
                const newX = Math.min(position.x, window.innerWidth - size.width - 20);
                const newY = Math.min(position.y, window.innerHeight - size.height - 20);
                setPosition({
                    x: newX,
                    y: newY
                });
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return ()=>window.removeEventListener('resize', handleResize);
    }, [
        position.x,
        position.y,
        size.width,
        size.height
    ]);
    return {
        // Chat context
        getCurrentChat,
        sendMessage,
        isGenerating,
        stopGeneration,
        createChat,
        currentChatId,
        clearChatMessages,
        currentChat,
        // State
        isAutoActivated,
        setIsAutoActivated,
        isMobile,
        setIsMobile,
        isCreatingChat,
        setIsCreatingChat,
        isMinimized,
        setIsMinimized,
        isMaximized,
        setIsMaximized,
        isCompact,
        setIsCompact,
        position,
        setPosition,
        size,
        setSize,
        currentView,
        setCurrentView,
        // Drag and drop
        isDragging,
        handleMouseDown,
        handleDragTouchStart,
        // Resizable
        isResizing,
        handleResizeStart,
        // Pinch zoom
        isPinching,
        handlePinchStart
    };
};
